import React, { useState } from 'react';
import styled from 'styled-components';
import { createProject } from '../utils/firestore';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faSpinner } from '@fortawesome/free-solid-svg-icons';

const ModalBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.3);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background-color: var(--surface-color);
  color: var(--text-color);
  padding: var(--space-6);
  border-radius: var(--radius-xl);
  width: 90%;
  max-width: 450px;
  box-shadow: var(--shadow-xl);
  position: relative;
  border: 1px solid var(--border-color-light);
`;

const CloseButton = styled.button`
  position: absolute;
  top: var(--space-3);
  right: var(--space-3);
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-lg);
  width: 32px;
  height: 32px;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &:hover {
    background: var(--error-50);
    color: var(--error-600);
    border-color: var(--error-300);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
`;

const Label = styled.label`
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  font-weight: 500;
  font-size: var(--text-sm);
  color: var(--text-color);
`;

const Input = styled.input`
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }
`;

const TextArea = styled.textarea`
  padding: var(--space-3) var(--space-4);
  border: 1px solid var(--border-color-light);
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-family: var(--font-family);
  font-weight: 400;
  background-color: var(--surface-color);
  color: var(--text-color);
  resize: vertical;
  min-height: 120px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);
  line-height: 1.6;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: 0 0 0 3px var(--primary-100), var(--shadow-md);
    transform: translateY(-1px);
  }

  &:hover:not(:focus) {
    border-color: var(--primary-300);
  }
`;

const Button = styled.button`
  padding: var(--space-3) var(--space-5);
  background-color: var(--primary-600);
  color: white;
  border: none;
  border-radius: var(--radius-lg);
  font-size: var(--text-base);
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: var(--shadow-sm);

  &:hover {
    background-color: var(--primary-700);
    transform: translateY(-1px);
    box-shadow: var(--shadow-md);
  }

  &:active {
    transform: translateY(0);
    box-shadow: var(--shadow-sm);
  }

  &:focus-visible {
    outline: 2px solid var(--primary-600);
    outline-offset: 2px;
  }
`;

const ErrorMessage = styled.div`
  color: var(--error-600);
  margin-top: var(--space-4);
  padding: var(--space-3);
  background-color: var(--error-50);
  border-radius: var(--radius-lg);
  border: 1px solid var(--error-200);
  font-size: var(--text-sm);
  font-weight: 500;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  border-radius: var(--radius-xl);
`;

function ProjectCreationForm({ isOpen, onClose, onCreateProject, userId }) {
  const [projectName, setProjectName] = useState('');
  const [projectDescription, setProjectDescription] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (projectName.trim() && userId) {
      setIsLoading(true);
      setError('');
      try {
        const projectId = await createProject(userId, {
          name: projectName.trim(),
          description: projectDescription.trim()
        });
        onCreateProject(projectId);
        onClose();
      } catch (error) {
        console.error('Failed to create project:', error);
        setError('Failed to create project. Please try again.');
      } finally {
        setIsLoading(false);
      }
    }
  };

  if (!isOpen) return null;

  return (
    <ModalBackground onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()} id="project-creation-modal">
        {isLoading && (
          <LoadingOverlay>
            <FontAwesomeIcon icon={faSpinner} spin size="3x" />
          </LoadingOverlay>
        )}
        <CloseButton onClick={onClose} id="close-project-creation-button">
          <FontAwesomeIcon icon={faTimes} />
        </CloseButton>
        <h2 style={{
          marginBottom: 'var(--space-6)',
          textAlign: 'center',
          color: 'var(--primary-color)',
          fontSize: 'var(--text-xl)',
          fontWeight: '500',
          fontFamily: 'var(--font-heading-bold)'
        }}>Create New Project</h2>
        <Form onSubmit={handleSubmit}>
          <Label>
            Project Name:
            <Input
              id="project-name-input"
              type="text"
              value={projectName}
              onChange={(e) => setProjectName(e.target.value)}
              required
            />
          </Label>
          <Label>
            Project Description:
            <TextArea
              id="project-description-input"
              value={projectDescription}
              onChange={(e) => setProjectDescription(e.target.value)}
              rows="4"
            />
          </Label>
          <Button type="submit" id="create-project-button" disabled={isLoading}>
            {isLoading ? 'Creating...' : 'Create Project'}
          </Button>
        </Form>
        {error && <ErrorMessage>{error}</ErrorMessage>}
      </ModalContent>
    </ModalBackground>
  );
}

export default ProjectCreationForm;